import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Header from '../components/Header/Header';
import Button from '../components/Button/Button';
import Toolbar from '../components/Toolbar/Toolbar';
import {
    LogoutIcon,
    ChevronUpIcon,
    InfoIcon,
    SettingsIcon,
    DrawPolygonIcon,
    BuildingIcon,
    DrawLineIcon,
    PinIcon,
    LayersIcon,
    ImportIcon,
    ExportIcon,
    RulerIcon,
    CrosshairIcon,
    ListIcon,
    PrintIcon,
    ExcelIcon,
    EditIcon,
    ExternalLinkIcon,
    AlertTriangleIcon,
    MapIcon,
    GlobeIcon,
    BoundaryIcon,
    TextIcon,
    ViewIcon
} from '../components/Icon';
import styles from './Pages.module.css';

// Локальные SVG иконки для разделов, где нет подходящих в Icon
const DatabaseIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <ellipse cx="12" cy="5" rx="9" ry="3" />
        <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3" />
        <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" />
    </svg>
);

const WrenchIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
    </svg>
);

// Группа "Справка" для всех режимов
const helpGroup = {
    label: 'Справка',
    items: [
        {
            id: 'help',
            label: 'Помощь',
            icon: <InfoIcon size={24} />,
            title: 'Помощь'
        },
    ]
};

// Демонстрационная конфигурация инструментов (копия из AdminDashboardPage)
const demoToolsConfig = {
    drawing: [
        {
            label: 'Рисование',
            items: [
                { id: 'draw-plot', label: 'Участки', icon: <DrawPolygonIcon size={18} />, title: 'Рисовать участки' },
                { id: 'draw-building', label: 'Здания', icon: <BuildingIcon size={18} />, title: 'Рисовать здания' },
                { id: 'draw-line', label: 'Линии', icon: <DrawLineIcon size={18} />, title: 'Рисовать линейные объекты' },
                { id: 'draw-marker', label: 'Метки', icon: <PinIcon size={18} />, title: 'Ставить метки' },
            ]
        },
        {
            label: 'Подложка',
            items: [
                { id: 'basemap', label: 'Подложка', icon: <LayersIcon size={18} />, title: 'Управление подложкой' },
            ]
        },
        {
            label: 'Данные',
            items: [
                { id: 'import', label: 'Импорт', icon: <ImportIcon size={18} />, title: 'Импорт данных' },
                { id: 'export', label: 'Экспорт', icon: <ExportIcon size={18} />, title: 'Экспорт данных' },
            ]
        },
        helpGroup
    ],
    data: [
        {
            label: 'Измерения',
            items: [
                { id: 'measure-ruler', label: 'Линейка', icon: <RulerIcon size={18} />, title: 'Измерить расстояние' },
                { id: 'measure-area', label: 'Площадь', icon: <DrawPolygonIcon size={18} />, title: 'Измерить площадь' },
                { id: 'measure-coords', label: 'Координаты', icon: <CrosshairIcon size={18} />, title: 'Координаты точки' },
            ]
        },
        {
            label: 'Работа с данными',
            items: [
                { id: 'legend', label: 'Легенда', icon: <ListIcon size={18} />, title: 'Легенда' },
                { id: 'object-manager', label: 'Менеджер объектов', icon: <SettingsIcon size={18} />, title: 'Менеджер объектов' },
            ]
        },
        helpGroup
    ],
    export: [
        {
            label: 'Настройка отображения',
            items: [
                {
                    id: 'text-plots',
                    label: 'Тексты участков',
                    icon: (
                        <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                            <BoundaryIcon size={18} />
                            <TextIcon size={12} style={{ position: 'absolute', right: -2, bottom: -2, stroke: 'var(--color-primary)', fill: 'var(--color-surface)' }} />
                        </div>
                    ),
                    title: 'Настроить тексты участков'
                },
                {
                    id: 'text-buildings',
                    label: 'Тексты зданий',
                    icon: (
                        <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                            <BuildingIcon size={18} />
                            <TextIcon size={12} style={{ position: 'absolute', right: -2, bottom: -2, stroke: 'var(--color-primary)', fill: 'var(--color-surface)' }} />
                        </div>
                    ),
                    title: 'Настроить тексты зданий'
                },
                { id: 'legend-settings', label: 'Настройка легенды', icon: <SettingsIcon size={18} />, title: 'Настроить легенду для печати' },
            ]
        },
        {
            label: 'Экспорт',
            items: [
                { id: 'export-print', label: 'Печать', icon: <PrintIcon size={18} />, title: 'Экспорт для печати' },
                { id: 'export-excel', label: 'Excel', icon: <ExcelIcon size={18} />, title: 'Экспорт в Excel' },
            ]
        },
        helpGroup
    ]
};

// Детали инструментов для интерактивных разделов
const toolDetails = {
    // Инструменты рисования
    'draw-plot': {
        title: 'Инструмент "Участки"',
        description: 'Этот инструмент позволяет рисовать на карте полигоны, которые представляют собой земельные участки. Вы можете создавать участки любой формы. Система автоматически рассчитает их площадь. Чтобы завершить рисование, просто кликните дважды или нажмите на первую точку, с которой начали.',
        gif: '/gifs/tools/draw-plot.gif'
    },
    'draw-building': {
        title: 'Инструмент "Здания"',
        description: 'Используйте этот инструмент для нанесения на карту зданий и сооружений. Как и в случае с участками, площадь застройки будет рассчитана автоматически. Вы сможете присвоить зданию категорию (например, жилое или коммерческое), и оно окрасится в соответствующий цвет.',
        gif: '/gifs/tools/draw-building.gif'
    },
    'draw-line': {
        title: 'Инструмент "Линии"',
        description: 'Этот инструмент предназначен для рисования линейных объектов, таких как дороги, трубопроводы или границы. Система автоматически измерит длину нарисованной линии. Вы также сможете выбрать её стиль, например, сплошная или пунктирная.',
        gif: '/gifs/tools/draw-line.gif'
    },
    'draw-marker': {
        title: 'Инструмент "Метки"',
        description: 'С помощью меток можно отмечать на карте важные точки, например, достопримечательности, входы или точки сбора. Вы можете загрузить для метки любую иконку, чтобы сделать карту более наглядной.',
        gif: '/gifs/tools/draw-marker.gif'
    },
    // Инструменты измерений
    'measure-ruler': {
        title: 'Инструмент "Линейка"',
        description: '«Линейка» позволяет измерять расстояния прямо на карте. Просто кликайте по карте, чтобы проложить маршрут, а система покажет его общую длину. Чтобы завершить измерение, кликните дважды. Это удобно для планирования и оценки.',
        gif: '/gifs/tools/measure-ruler.gif'
    },
    'measure-area': {
        title: 'Инструмент "Площадь"',
        description: 'Этот инструмент работает так же, как «Линейка», но измеряет не длину, а площадь. Нарисуйте на карте замкнутую фигуру, и система автоматически рассчитает её площадь в квадратных метрах, гектарах или квадратных километрах.',
        gif: '/gifs/tools/measure-area.gif'
    },
    'measure-coords': {
        title: 'Инструмент "Координаты"',
        description: 'Если вам нужно узнать точные географические координаты какой-либо точки на карте, используйте этот инструмент. Просто кликните в нужном месте, и в боковой панели появятся координаты, которые можно легко скопировать. Также показывает объекты в этой точке.',
        gif: '/gifs/tools/measure-coords.gif'
    },
    // Инструменты данных
    'import': {
        title: 'Инструмент "Импорт"',
        description: 'Если у вас уже есть готовые геоданные в формате GeoJSON, вы можете легко загрузить их в систему. Это позволяет быстро перенести на карту большое количество объектов, например, из другой программы или от подрядчика.',
        gif: '/gifs/tools/import.gif'
    },
    'export': {
        title: 'Инструмент "Экспорт"',
        description: 'Этот инструмент позволяет выгрузить все видимые на карте объекты в файл формата GeoJSON. Это удобно для резервного копирования, обмена данными с коллегами или для использования в других ГИС-системах.',
        gif: '/gifs/tools/export.gif'
    },
    'legend': {
        title: 'Инструмент "Легенда"',
        description: 'Легенда — это инструмент для настройки отображения объектов на карте. С ее помощью можно создавать категории с уникальными цветами и штриховкой.',
        gif: '/gifs/tools/legend.gif'
    },
    'object-manager': {
        title: 'Менеджер объектов',
        description: 'Менеджер объектов представляет все данные вашей карты в удобной таблице. С его помощью вы легко найдете нужные объекты через поиск и фильтры, отредактируете их свойства и настроите видимость на карте.',
        gif: '/gifs/tools/object-manager.gif'
    }
};

// Компонент для демонстрации кнопок инструментов
const ToolButtonDemo = ({ icon, label }) => (
    <button className={styles.toolButtonDemo} title={label}>
        <span className={styles.toolIconDemo}>{icon}</span>
        <span>{label}</span>
    </button>
);

// Группа инструментов
const ToolGroupDemo = ({ label, tools }) => (
    <div className={styles.toolGroupDemo}>
        {label && <div className={styles.toolGroupLabel}>{label}</div>}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--spacing-sm)' }}>
            {tools.map((tool, idx) => (
                <ToolButtonDemo key={idx} icon={tool.icon} label={tool.label} />
            ))}
        </div>
    </div>
);

const AdminDocumentationPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [activeDrawingTool, setActiveDrawingTool] = React.useState('draw-plot');
    const [activeMeasurementTool, setActiveMeasurementTool] = React.useState('measure-ruler');
    const [activeDataTool, setActiveDataTool] = React.useState('import');
    const [isAuthenticated, setIsAuthenticated] = React.useState(false);
    const [isLoadingAuth, setIsLoadingAuth] = React.useState(true);

    const API_URL = '/api';

    React.useEffect(() => {
        const checkAuth = async () => {
            try {
                const response = await fetch(`${API_URL}/auth/check`, { 
                    credentials: 'include' 
                });
                const data = await response.json();
                if (!data.authenticated) {
                    navigate('/map/admin', { replace: true });
                } else {
                    setIsAuthenticated(true);
                }
            } catch (err) {
                console.error('Auth check failed:', err);
                navigate('/map/admin', { replace: true });
            } finally {
                setIsLoadingAuth(false);
            }
        };

        checkAuth();
    }, [navigate, location]);

    if (isLoadingAuth) {
        return (
            <div style={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                height: '100vh' 
            }}>
                <p>Проверка авторизации...</p>
            </div>
        );
    }

    if (!isAuthenticated) {
        return null;
    }

    const navItems = [
        {
            id: 'back',
            label: 'Назад',
            icon: <ChevronUpIcon size={20} />,
            onClick: () => navigate('/map/admin/dashboard')
        }
    ];

    const headerActions = [
        {
            id: 'logout',
            icon: <LogoutIcon size={20} />,
            title: 'Выйти из системы',
            onClick: async () => {
                if (window.confirm('Выйти из системы?')) {
                    try {
                        await fetch(`${API_URL}/auth/logout`, {
                            method: 'POST',
                            credentials: 'include'
                        });
                    } catch (err) {
                        console.error('Logout error:', err);
                    }
                    navigate('/map/admin');
                }
            }
        }
    ];

    const sections = [
        {
            title: 'Общее описание системы',
            icon: <InfoIcon size={24} />,
            render: () => (
                <>
                    <p><strong>IChis SPD</strong> — это ваш цифровой помощник для работы с картой территории. Представьте, что у вас есть интерактивный план, на котором можно не только видеть все объекты, но и легко их изменять, добавлять новые и анализировать данные. Система создана для того, чтобы сделать управление земельными участками, зданиями и коммуникациями простым и наглядным.</p>
                    <h4>Ключевые функции:</h4>
                    <ul>
                        <li><strong>Интерактивная карта:</strong> Удобная и быстрая карта, на которой всё видно.</li>
                        <li><strong>Рисование объектов:</strong> Простое создание участков, зданий, дорог и меток прямо на карте.</li>
                        <li><strong>Управление слоями:</strong> Возможность включать и выключать отображение разных типов объектов.</li>
                        <li><strong>Работа с данными:</strong> Лёгкий импорт готовых данных и экспорт вашей работы для других программ.</li>
                        <li><strong>Инструменты измерений:</strong> Точное измерение расстояний и площадей без сложных расчётов.</li>
                        <li><strong>Админ-панель:</strong> Мощный инструмент для детального управления всеми объектами на карте.</li>
                    </ul>
                </>
            )
        },
        {
            title: 'Режимы работы',
            icon: <SettingsIcon size={24} />,
            render: () => (
                <div>
                    <p>В верхней части экрана находится переключатель режимов. Каждый режим предоставляет свой набор инструментов для определённых задач.</p>
                    
                    <h4>1. Режим «Рисование»</h4>
                    <p>Это ваш основной рабочий режим. Здесь вы можете создавать новые объекты на карте: участки, здания, дороги и метки. Все инструменты для творчества находятся здесь.</p>
                    <div style={{ margin: 'var(--spacing-md) 0', display: 'flex', justifyContent: 'center' }}>
                        <Toolbar
                            tools={demoToolsConfig.drawing}
                            activeTool={null}
                            onToolChange={() => {}}
                            orientation="horizontal"
                            showLabels={false}
                        />
                    </div>
                    
                    <h4>2. Режим «Данные»</h4>
                    <p>Этот режим предназначен для анализа и управления информацией. Здесь вы найдёте инструменты для измерения расстояний и площадей, а также для работы с легендой и менеджером объектов.</p>
                    <div style={{ margin: 'var(--spacing-md) 0', display: 'flex', justifyContent: 'center' }}>
                        <Toolbar
                            tools={demoToolsConfig.data}
                            activeTool={null}
                            onToolChange={() => {}}
                            orientation="horizontal"
                            showLabels={false}
                        />
                    </div>
                    
                    <h4>3. Режим «Экспорт»</h4>
                    <p>Когда карта готова, этот режим поможет подготовить её для печати или экспорта в другие форматы. Здесь можно настроить отображение текстов и легенды.</p>
                    <div style={{ margin: 'var(--spacing-md) 0', display: 'flex', justifyContent: 'center' }}>
                        <Toolbar
                            tools={demoToolsConfig.export}
                            activeTool={null}
                            onToolChange={() => {}}
                            orientation="horizontal"
                            showLabels={true}
                        />
                    </div>
                </div>
            )
        },
        {
            title: 'Инструменты рисования',
            icon: <DrawPolygonIcon size={24} />,
            render: () => (
                <>
                    <p>В режиме «Рисование» вы получаете доступ к инструментам, которые позволяют "оживить" вашу карту, добавляя на неё различные объекты. Это основа вашей работы.</p>
                    <p><strong>Совет:</strong> Чтобы рисовать прямые линии и объекты, удерживайте клавишу Shift.</p>

                    <h4>Доступные инструменты (нажмите, чтобы узнать больше):</h4>
                    <div style={{ margin: 'var(--spacing-md) 0' }}>
                        <Toolbar
                            tools={[demoToolsConfig.drawing[0], helpGroup]}
                            activeTool={activeDrawingTool}
                            onToolChange={(toolId) => {
                                if (toolId === 'help') return;
                                setActiveDrawingTool(prev => prev === toolId ? null : toolId);
                            }}
                            orientation="horizontal"
                            showLabels={false}
                        />
                    </div>
                    {activeDrawingTool && toolDetails[activeDrawingTool] && (
                        <div style={{
                            display: 'flex',
                            gap: 'var(--spacing-xl)',
                            marginTop: 'var(--spacing-lg)',
                            padding: 'var(--spacing-md)',
                            background: 'var(--color-background)',
                            borderRadius: 'var(--radius-md)',
                            border: '1px solid var(--color-border)'
                        }}>
                            <div style={{ flex: 1 }}>
                                <h5>{toolDetails[activeDrawingTool].title}</h5>
                                <p>{toolDetails[activeDrawingTool].description}</p>
                            </div>
                            <div style={{
                                flex: 1,
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center'
                            }}>
                                <img
                                    src={toolDetails[activeDrawingTool].gif}
                                    alt={toolDetails[activeDrawingTool].title}
                                    style={{
                                        maxWidth: '100%',
                                        maxHeight: '300px',
                                        borderRadius: 'var(--radius-md)',
                                        objectFit: 'contain'
                                    }}
                                />
                            </div>
                        </div>
                    )}
                </>
            )
        },
        {
            title: 'Измерения',
            icon: <RulerIcon size={24} />,
            render: () => (
                <div>
                    <p>Эти инструменты помогут вам получить точные цифры прямо с карты, будь то длина дороги или площадь участка. Это незаменимо для планирования и анализа.</p>
                    <p><strong>Совет:</strong> Чтобы измерить участок, здание или дорогу, выделите нужный объект и выберите соответствующий инструмент измерения.</p>

                    <h4>Доступные инструменты (нажмите, чтобы узнать больше):</h4>
                    <div style={{ margin: 'var(--spacing-md) 0' }}>
                        <Toolbar
                            tools={[demoToolsConfig.data[0], helpGroup]}
                            activeTool={activeMeasurementTool}
                            onToolChange={(toolId) => {
                                if (toolId === 'help') return;
                                setActiveMeasurementTool(prev => prev === toolId ? null : toolId);
                            }}
                            orientation="horizontal"
                            showLabels={false}
                        />
                    </div>
                    {activeMeasurementTool && toolDetails[activeMeasurementTool] && (
                        <div style={{
                            display: 'flex',
                            gap: 'var(--spacing-xl)',
                            marginTop: 'var(--spacing-lg)',
                            padding: 'var(--spacing-md)',
                            background: 'var(--color-background)',
                            borderRadius: 'var(--radius-md)',
                            border: '1px solid var(--color-border)'
                        }}>
                            <div style={{ flex: 1 }}>
                                <h5>{toolDetails[activeMeasurementTool].title}</h5>
                                <p>{toolDetails[activeMeasurementTool].description}</p>
                            </div>
                            <div style={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                <img
                                    src={toolDetails[activeMeasurementTool].gif}
                                    alt={toolDetails[activeMeasurementTool].title}
                                    style={{ maxWidth: '100%', maxHeight: '300px', borderRadius: 'var(--radius-md)', objectFit: 'contain' }}
                                />
                            </div>
                        </div>
                    )}
                </div>
            )
        },
        {
            title: 'Управление данными',
            icon: <DatabaseIcon />,
            render: () => (
                <div>
                    <p>Эти инструменты предназначены для работы с информацией, которая стоит за объектами на карте. Вы можете загружать данные, выгружать их, а также управлять отображением и свойствами объектов.</p>
                    <p><strong>Совет:</strong> Перед импортом данных убедитесь, что ваш файл в формате GeoJSON и содержит `FeatureCollection`. Это стандарт для геоданных, который гарантирует совместимость.</p>

                    <h4>Доступные инструменты (нажмите, чтобы узнать больше):</h4>
                    <div style={{ margin: 'var(--spacing-md) 0' }}>
                        <Toolbar
                            tools={[demoToolsConfig.data[1], demoToolsConfig.drawing[2], helpGroup]}
                            activeTool={activeDataTool}
                            onToolChange={(toolId) => {
                                if (toolId === 'help') return;
                                setActiveDataTool(prev => prev === toolId ? null : toolId);
                            }}
                            orientation="horizontal"
                            showLabels={false}
                        />
                    </div>
                    {activeDataTool && toolDetails[activeDataTool] && (
                        <div style={{
                            display: 'flex',
                            gap: 'var(--spacing-xl)',
                            marginTop: 'var(--spacing-lg)',
                            padding: 'var(--spacing-md)',
                            background: 'var(--color-background)',
                            borderRadius: 'var(--radius-md)',
                            border: '1px solid var(--color-border)'
                        }}>
                            <div style={{ flex: 1 }}>
                                <h5>{toolDetails[activeDataTool].title}</h5>
                                <p>{toolDetails[activeDataTool].description}</p>
                            </div>
                            <div style={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                <img
                                    src={toolDetails[activeDataTool].gif}
                                    alt={toolDetails[activeDataTool].title}
                                    style={{ maxWidth: '100%', maxHeight: '300px', borderRadius: 'var(--radius-md)', objectFit: 'contain' }}
                                />
                            </div>
                        </div>
                    )}
                    {/* <h3>Импорт данных</h3>
                    <ul>
                        <li>Поддерживается формат GeoJSON FeatureCollection</li>
                        <li>Файл должен содержать массив features с геометрией и свойствами</li>
                        <li>Объекты импортируются в текущую карту</li>
                        <li>Проверяется валидность геометрии</li>
                    </ul>
                    <h3>Экспорт данных</h3>
                    <ul>
                        <li>Экспорт всех видимых объектов в GeoJSON</li>
                        <li>Сохранение всех свойств объектов</li>
                        <li>Возможность выбора формата (JSON)</li>
                    </ul>
                    <h3>Менеджер объектов</h3>
                    <ul>
                        <li>Табличный просмотр всех объектов</li>
                        <li>Фильтрация по типу, категории, статусу</li>
                        <li>Массовое редактирование свойств</li>
                        <li>Быстрое переключение видимости</li>
                        <li>Сортировка и поиск</li>
                    </ul> */}
                </div>
            )
        },
        {
            title: 'Легенда и категории',
            icon: <ListIcon size={24} />,
            render: () => (
                <div>
                    <p>Легенда — это ключ к пониманию вашей карты. Она не только объясняет, что означает каждый цвет или иконка, но и позволяет управлять видимостью объектов. Вы можете создавать свои категории, задавать им цвета и стили.</p>
                    <h4>Категории объектов:</h4>
                    <ul>
                        <li><strong>Участки:</strong> Разделение участков по назначению (например, "Коммерческие", "Жилые", "Парковые зоны").</li>
                        <li><strong>Здания:</strong> Классификация зданий (например, "Офисы", "Торговые центры", "Склады").</li>
                        <li><strong>Дороги:</strong> Типы дорог (например, "Главные", "Второстепенные").</li>
                        <li><strong>Метки:</strong> Группировка точечных объектов (например, "Входы", "Парковки").</li>
                    </ul>
                    <h4>Статусы объектов:</h4>
                    <p>Статусы помогают отслеживать жизненный цикл объектов на территории.</p>
                    <ul>
                        <li><ViewIcon size={14} style={{color: 'var(--color-status-for-sale)', display: 'inline-block', verticalAlign: 'middle'}}/> <strong>В продаже:</strong> Объекты, доступные для покупки или аренды.</li>
                        <li><ViewIcon size={14} style={{color: 'var(--color-status-is-building)', display: 'inline-block', verticalAlign: 'middle'}}/> <strong>В строительстве:</strong> Объекты, которые находятся на этапе строительства.</li>
                        <li><ViewIcon size={14} style={{color: 'var(--color-status-is-planned)', display: 'inline-block', verticalAlign: 'middle'}}/> <strong>В планировании:</strong> Объекты, которые пока существуют только в проекте.</li>
                    </ul>
                </div>
            )
        },
        {
            title: 'Базовая карта и подложки',
            icon: <LayersIcon size={24} />,
            render: () => (
                <div>
                    <p>Базовая карта (или подложка) — это фон, на котором отображаются все ваши объекты. Вы можете выбрать тот, который лучше всего подходит для ваших задач.</p>
                    <h4>Доступные стили:</h4>
                    <ToolGroupDemo 
                        tools={[
                            { icon: <MapIcon size={18} />, label: 'Схема' },
                            { icon: <GlobeIcon size={18} />, label: 'Спутник' },
                            { icon: <LayersIcon size={18} />, label: 'Своя' },
                        ]}
                    />
                    <ul>
                        <li><strong>Схема:</strong> Карта с упрощённой графикой, акцентирующая внимание на навигации и структуре местности.</li>
                        <li><strong>Спутник:</strong> Реалистичное изображение местности со спутника. Помогает лучше ориентироваться и видеть реальные объекты.</li>
                        <li><strong>Своя:</strong> Простая и чистая карта, похожая на Яндекс.Карты или Google Maps. Идеальна для большинства задач.</li>
                    </ul>
                    <p>Ваши объекты (участки, здания) всегда будут отображаться поверх выбранной базовой карты.</p>
                </div>
            )
        },
        {
            title: 'Экспорт и печать',
            icon: <PrintIcon size={24} />,
            render: () => (
                <div>
                    <p><strong>Примечание:</strong> Этот режим и его инструменты доступны только в <a href="#section-8">расширенной версии</a> системы.</p>
                    <p>Режим «Экспорт» предназначен для подготовки карты и данных для печати или использования в отчётах. Он позволяет создавать высококачественные PDF и SVG файлы, выгружать данные в Excel и тонко настраивать внешний вид печатной версии карты.</p>
                </div>
            )
        },
        {
            title: 'Расширенная версия',
            icon: <WrenchIcon size={24} />,
            render: () => (
                <div>
                    <p>Помимо базового функционала, существует расширенная версия системы, которая включает в себя профессиональные инструменты для более сложных задач:</p>
                    <ul>
                        <li><strong>Продвинутое редактирование геометрии:</strong> Возможность вырезать один участок из другого или объединять несколько участков в один.</li>
                        <li><strong>Высококачественная печать:</strong> Экспорт карты в форматы SVG и PDF для полиграфии.</li>
                        <li><strong>Кластеризация:</strong> Автоматическое группирование близко расположенных меток в один значок при отдалении карты.</li>
                        <li><strong>Интеграция с НСПД:</strong> Автоматическая сверка и обновление данных с Национальной системой пространственных данных.</li>
                        <li><strong>История действий:</strong> Возможность отменять и повторять свои действия (Ctrl+Z / Ctrl+Y).</li>
                        <li><strong>Групповое редактирование:</strong> Изменение свойств сразу у нескольких выбранных объектов.</li>
                        <li><strong>Гибкая настройка экспорта:</strong> Управление расположением текстов и легенды на печатной версии карты.</li>
                        <li><strong>Геопривязка растровых подложек:</strong> Точное позиционирование сканированных планов или аэрофотоснимков на карте.</li>
                        <li><strong>Мультикарты:</strong> Управление несколькими независимыми картами в одной админ-панели.</li>
                        <li><strong>Управление командой:</strong> Создание пользователей с разными ролями и правами доступа, отслеживание истории изменений.</li>
                    </ul>
                </div>
            )
        },
        {
            title: 'Панель редактирования',
            icon: <EditIcon size={24} />,
            render: () => (
                <div>
                    <p>Когда вы создаёте новый объект или выбираете существующий, справа открывается панель редактирования. В ней вы можете задать или изменить всю информацию об объекте.</p>
                    <h4>Основные поля:</h4>
                    <ul>
                        <li><strong>Название:</strong> Уникальное имя объекта, например, "Участок №15" или "Главный офис".</li>
                        <li><strong>Категория:</strong> К какой группе относится объект (например, "Жилые здания"). От этого зависит его цвет на карте.</li>
                        <li><strong>Статус:</strong> Текущее состояние объекта ("В продаже", "В строительстве" и т.д.).</li>
                        <li><strong>Другие свойства:</strong> Вы можете добавлять любые другие характеристики, например, "Этажность", "Материал стен" или "Ответственный".</li>
                    </ul>
                </div>
            )
        },
        {
            title: 'Менеджер объектов',
            icon: <SettingsIcon size={24} />,
            render: () => (
                <div>
                    <p>Менеджер объектов — это мощный инструмент, который представляет все ваши объекты в виде интерактивного списка. Он идеально подходит для работы с большим количеством данных.</p>
                    <h4>Ключевые возможности:</h4>
                    <ul>
                        <li><strong>Быстрый поиск и фильтрация:</strong> Легко находите нужные объекты по названию, категории или любому другому свойству.</li>
                        <li><strong>Сортировка:</strong> Упорядочивайте данные по любому столбцу, например, по площади или дате создания.</li>
                        <li><strong>Массовые операции:</strong> Выбирайте несколько объектов и меняйте их видимость, категорию или удаляйте их одним действием.</li>
                        <li><strong>Быстрый переход:</strong> Кликнув на объект в таблице, вы мгновенно переместитесь к нему на карте.</li>
                    </ul>
                </div>
            )
        },
        {
            title: 'Советы и рекомендации',
            icon: <AlertTriangleIcon size={24} />,
            render: () => (
                <div>
                    <h4>Навигация по карте:</h4>
                    <ul>
                        <li><strong>Приближение/отдаление:</strong> Используйте колесо мыши или кнопки "+" и "-".</li>
                        <li><strong>Вращение и наклон:</strong> Зажмите правую кнопку мыши и двигайте её, чтобы вращать карту. Если при этом зажать клавишу `Shift`, вы сможете изменять наклон карты.</li>
                    </ul>
                    <h4>Рисование объектов:</h4>
                    <ul>
                        <li>Чтобы завершить рисование полигона или линии, сделайте двойной клик или просто кликните на начальную точку.</li>
                        <li>Если вы ошиблись и поставили точку не туда, нажмите правую кнопку мыши, чтобы отменить последнее действие.</li>
                    </ul>
                </div>
            )
        },
        {
            title: 'Структура данных (для продвинутых)',
            icon: <DatabaseIcon />,
            render: () => (
                <div>
                    <p>Система использует стандартный формат <strong>GeoJSON</strong> для хранения и обмена геопространственными данными. Это текстовый формат, который понятен большинству картографических программ.</p>
                    <h4>Пример структуры объекта:</h4>
                    <pre>{`{
  "type": "FeatureCollection",
  "features": [
    {
      "type": "Feature",
      "id": "unique_id",
      "geometry": {
        "type": "Polygon",
        "coordinates": [[[lon, lat], ...]]
      },
      "properties": {
        "name": "Название",
        "type": "plot",
        "category": "commercial",
        "status": "for_sale",
        "color": "#ffc107",
        "area": 1500,
        "floors": 3,
        "year": 2020
      }
    }
  ]
}`}</pre>
                    <h4>Основные типы геометрии:</h4>
                    <ul>
                        <li><strong>Point:</strong> Одиночная точка на карте (используется для меток).</li>
                        <li><strong>LineString:</strong> Линия, состоящая из нескольких точек (для дорог, рек, коммуникаций).</li>
                        <li><strong>Polygon:</strong> Замкнутая фигура, представляющая площадь (для участков, зданий, озёр).</li>
                    </ul>
                </div>
            )
        }
    ];

    return (
        <div className={`${styles.adminDashboardPage} ${styles.documentationPage}`}>
            <Header
                title="IChis"
                subtitle="Документация IChis SPD v1.0.0b"
                logo="SPD"
                navItems={navItems}
                actions={headerActions}
                user={{ name: 'Администратор', avatar: null }}
                sticky
            />

            <div className={styles.dashboardContainer}>
                <div className={styles.documentationContent}>
                    <div className={styles.documentationHeader}>
                        <h1>Руководство пользователя IChis SPD</h1>
                        <p>Подробное руководство по использованию системы управления территорией</p>
                    </div>

                    <div className={styles.documentationNav}>
                        {sections.map((section, index) => (
                            <a
                                key={index}
                                href={`#section-${index}`}
                                className={styles.docNavItem}
                            >
                                <span className={styles.docNavIcon}>{section.icon}</span>
                                <span className={styles.docNavLabel}>{section.title}</span>
                            </a>
                        ))}
                    </div>

                    <div className={styles.documentationSections}>
                        {sections.map((section, index) => (
                            <section key={index} id={`section-${index}`} className={styles.docSection}>
                                <div className={styles.docSectionHeader}>
                                    <span className={styles.docSectionIcon}>{section.icon}</span>
                                    <h2>{section.title}</h2>
                                </div>
                                <div className={styles.docSectionContent}>
                                    {section.render()}
                                </div>
                            </section>
                        ))}
                    </div>

                    <div className={styles.documentationFooter}>
                        <h3>Нужна помощь?</h3>
                        <p>Если у вас остались вопросы или вы столкнулись с проблемой, свяжитесь с разработчиком.</p>
                        <div className={styles.socialLinks}>
                            <a href="mailto:i@ichistin.ru" className={styles.socialLink} aria-label="Email">
                                <svg className={styles.socialIcon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>
                                </svg>
                            </a>
                            <a href="https://t.me/Chistin_I" target="_blank" rel="noopener noreferrer" className={styles.socialLink} aria-label="Telegram">
                                <svg className={styles.socialIcon} viewBox="0 0 192 192" xmlns="http://www.w3.org/2000/svg" fill="none">
                                    <path stroke="currentColor" strokeWidth="12" d="M23.073 88.132s65.458-26.782 88.16-36.212c8.702-3.772 38.215-15.843 38.215-15.843s13.621-5.28 12.486 7.544c-.379 5.281-3.406 23.764-6.433 43.756-4.54 28.291-9.459 59.221-9.459 59.221s-.756 8.676-7.188 10.185c-6.433 1.509-17.027-5.281-18.919-6.79-1.513-1.132-28.377-18.106-38.214-26.404-2.649-2.263-5.676-6.79.378-12.071 13.621-12.447 29.891-27.913 39.728-37.72 4.54-4.527 9.081-15.089-9.837-2.264-26.864 18.483-53.35 35.835-53.35 35.835s-6.053 3.772-17.404.377c-11.351-3.395-24.594-7.921-24.594-7.921s-9.08-5.659 6.433-11.693Z"></path>
                                </svg>
                            </a>
                            <a href="https://vk.com/chistinivan" target="_blank" rel="noopener noreferrer" className={styles.socialLink} aria-label="VK">
                                <svg className={styles.socialIcon} viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
                                    <path fill="currentColor" d="M27.55,35.19V28.55c4.46.68,5.87,4.19,8.71,6.64H43.5a29.36,29.36,0,0,0-7.9-10.47c2.6-3.58,5.36-6.95,6.71-12.06H35.73c-2.58,3.91-3.94,8.49-8.18,11.51V12.66H18l2.28,2.82,0,10.05c-3.7-.43-6.2-7.2-8.91-12.87H4.5C7,20.32,12.26,37.13,27.55,35.19Z"></path>
                                </svg>
                            </a>
                        </div>
                        <div style={{ marginTop: 'var(--spacing-lg)', display: 'flex', justifyContent: 'center' }}>
                            <Button
                                variant="secondary"
                                icon={<InfoIcon size={16} />}
                                onClick={() => navigate('/map/admin/dashboard')}
                            >
                                Перейти в админ-панель
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdminDocumentationPage;
